/**
 * The platform ladder, as the console reads it.
 *
 * Its own module rather than a corner of `api.ts`, for the same reason the
 * Overview sentences are not in `Overview.tsx`: `api.ts` imports `config.ts`, which
 * reads `import.meta.env`, which does not exist under `node --test`. Logic that
 * cannot be loaded by the runner is logic nothing can execute — and this
 * particular logic decides what a menu offers, which is where the fault it fixes
 * came from.
 *
 * A copy of the chassis's ladder in `@theg1group/console-kit` `src/lib/access.ts`;
 * `roles.test.ts` guards drift. The copy is deliberate: importing the chassis would
 * pull its Cloudflare-typed bindings into the SPA. So the test reads `ROLES` out of
 * the installed package as text and asserts the whole matrix against it, and a rung
 * added there and forgotten here fails a test rather than quietly offering links the
 * API refuses.
 *
 * This never grants anything. Every governing route is refused server-side
 * against ttads' own tables, whatever the menu shows; this only decides what to
 * offer, so the console does not promise what the API will not deliver.
 */
export type Role = "super_admin" | "admin" | "manager" | "user";

const ROLE_RANK: Record<Role, number> = { user: 1, manager: 2, admin: 3, super_admin: 4 };

export function roleAtLeast(role: Role, floor: Role): boolean {
	return ROLE_RANK[role] >= ROLE_RANK[floor];
}

/**
 * The kit's `isPlatformAdmin` (`lib/principal.ts`: a platform seat at `admin` or above),
 * read off the standing the console already holds. `POST /api/v1/tiktok-accounts/sync`
 * refuses everyone else with a bare `forbidden` (routes/v1.ts), so the Accounts screen
 * offers the sync only to a platform admin (the TikTok console's map of the SPA's second
 * wave, audits/2026-10-20/SUMMARY.md at 09:59–10:00Z). A tenant has no platform seat,
 * whatever its team role. Like the rest of this file, it hides; it grants nothing.
 */
export function isPlatformAdmin(standing: { platform: { role: Role } | null }): boolean {
	return standing.platform !== null && roleAtLeast(standing.platform.role, "admin");
}
